import {
  Field,
  ImageField,
  Item,
  LinkField,
  NextImage,
  Placeholder,
  RichText,
  Text,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import ScLink from 'components/Generic/SitecoreWrappers/ScLink';
import RiskWarning, { RiskWarningProps } from 'components/Sections/RiskWarning/RiskWarning';
import { useSitecoreContext } from 'hooks/useSitecoreContext';
import { ComponentWithContextProps } from 'lib/component-props';
import { startTransition, useMemo, useState } from 'react';
import AnimateHeight, { Height } from 'react-animate-height';

type FooterProps = ComponentWithContextProps & {
  fields: {
    logo: ImageField;
    logoLink: LinkField;
    expandableBlockTitle: Field<string>;
    expandableBlockText: Field<string>;
    badge: ImageField;
    copyrightText: Field<string>;
    registeredText: Field<string>;
    items: Array<Item>;
    riskWarning: RiskWarningProps;
    expandedView: Field<boolean>;
  };
};

const Footer = (props: FooterProps): JSX.Element => {
  const [height, setHeight] = useState<Height>(0);

  const { sitecoreContext } = useSitecoreContext();
  const isExperienceEditor = sitecoreContext.pageEditing ?? false;

  const showPolicy = (): boolean => {
    return (
      props.fields.items.length > 0 ||
      !!props.rendering?.placeholders?.['abrdn-social-network']?.length ||
      !!props.fields.expandableBlockTitle?.value ||
      !!props.fields.copyrightText?.value ||
      isExperienceEditor
    );
  };

  const showSocialNetwork: boolean =
    !!props.rendering?.placeholders?.['abrdn-social-network']?.length || isExperienceEditor;

  const showExpandableContent: boolean | string | undefined =
    (props.fields.expandableBlockTitle && props.fields.expandableBlockTitle.value) ||
    (props.fields.copyrightText && props.fields.copyrightText.value) ||
    !!props.fields.badge?.value ||
    isExperienceEditor;

  const currentYear = (): string => {
    return new Date().getFullYear().toString().slice(-2);
  };

  const copyrightString = (): object => {
    return {
      ...props.fields.copyrightText,
      value: props.fields.copyrightText.value.replace('{token.currentYear}', currentYear),
    };
  };

  const isDropdownExpandedByDefault = !!props.fields.expandedView?.value;

  const showCopyrightText = (): void => {
    if (
      (props.fields.expandableBlockText && props.fields.expandableBlockText.value) ||
      isExperienceEditor
    ) {
      startTransition(() => {
        setTimeout(() => {
          window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
        }, 200);
      });
    }
  };

  const riskWarningsFields = useMemo(() => {
    return props.fields?.riskWarning?.fields;
  }, [props.fields?.riskWarning?.fields]);

  const getClassName =
    props.sitecoreContext.pageTheme?.toLowerCase() === 'investment'
      ? 'background-primary'
      : 'background-black';

  return (
    <section className="main-footer">
      {riskWarningsFields && <RiskWarning fields={props.fields?.riskWarning?.fields} />}
      <footer
        className={`flex footer-root py-5 py-md-6 py-xl-8 ${getClassName}`}
        role="contentinfo"
      >
        <div className="container">
          <div className="logo-wrapper mb-5 mb-md-6 mb-xl-8">
            <ScLink
              field={props.fields.logoLink}
              className="logo-wrapper__link"
              showExternalIcon={false}
              replaceWithSlot={true}
            >
              <NextImage
                field={props.fields.logo.value}
                width="170"
                height="30"
                alt="Aberdeen logo"
              />
            </ScLink>
          </div>
          {!!showPolicy && (
            <div className="policy-section pt-4">
              <div className="d-flex flex-column flex-md-row mb-3 mb-lg-2 mb-md-3 row">
                {(props.fields.items.length || isExperienceEditor) && (
                  <div className="col policies d-flex col-12 col-lg-9 flex-wrap flex-lg-wrap">
                    {props.fields.items.map((policy, index) => {
                      return (
                        <div
                          key={index}
                          className="mb-sm-2 mb-2 mb-md-1 me-lg-4 me-sm-3 align-items-center"
                        >
                          <ScLink
                            className="footer-link-small"
                            field={policy.fields.link as LinkField}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
                {showSocialNetwork && (
                  <div className="col col-12 col-lg-3 d-flex justify-content-start justify-content-lg-end mt-1 mt-sm-1 mt-md-2 mt-lg-0">
                    <Placeholder rendering={props.rendering} name="abrdn-social-network" />
                  </div>
                )}
              </div>
              {showExpandableContent && (
                <div className="row">
                  <div className="col col-12 d-flex flex-column flex-md-row align-items-start align-items-md-end mb-3">
                    <div className="d-flex flex-column">
                      <Text field={copyrightString()} tag="span" className="footnote" />
                      <Text field={props.fields.registeredText} tag="span" className="footnote" />
                    </div>
                    <NextImage
                      className="badge-wrapper mt-3 mt-md-0 ms-0 ms-md-3"
                      field={props.fields.badge}
                      width="48"
                      height="36"
                    />
                  </div>
                  {((props.fields.expandableBlockTitle &&
                    props.fields.expandableBlockTitle.value) ||
                    isExperienceEditor) && (
                    <div className="col col-12 col-md-8 d-flex">
                      <button
                        data-toggle="collapse"
                        aria-expanded={isDropdownExpandedByDefault}
                        aria-controls="copyright-content"
                        className={`btn text-link copyright-button d-flex align-items-end p-0 ${
                          !isDropdownExpandedByDefault && height === 0 ? 'collapsed' : 'expanded'
                        }`}
                        type="button"
                        onClick={() => {
                          showCopyrightText();
                          setHeight(height === 0 ? 'auto' : 0);
                        }}
                      >
                        <div className="d-flex align-items-center expandable">
                          <Text
                            className="expandable-title mb-0 font-size-6"
                            field={props.fields.expandableBlockTitle}
                          />
                          {((props.fields.expandableBlockText &&
                            props.fields.expandableBlockText.value) ||
                            isExperienceEditor) && <i className="bi bi-chevron-down ms-3" />}
                        </div>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <AnimateHeight id="copyright-content" height={height} duration={500}>
            <div className="copyright-dropdown">
              <div className="row">
                <div className="col">
                  <RichText field={props.fields.expandableBlockText} className="body-small" />
                </div>
              </div>
            </div>
          </AnimateHeight>
        </div>
      </footer>
    </section>
  );
};

export default withDatasourceCheck()<FooterProps>(Footer);
